import React from "react";
import SpecificQuestion from "./SpecificQuestion";
import { useSelector } from "react-redux";
import { getFeedbackForm } from "../../../redux/reducer/incbotFeedbackReducer";
import { QUESTION_TYPES } from "../../../constants";
import { isGenerator } from "framer-motion";

const SpecificQuestionSection = () => {
  const feedbackForm = useSelector(getFeedbackForm);
  const customQuestions = (feedbackForm?.questions?.data || [])?.filter(
    (question: any) =>
      question.questionType === QUESTION_TYPES.CUSTOM ||
      question.questionType === QUESTION_TYPES.AI_GENERATED
  );
  const isSkillBased = feedbackForm?.isSkillBased || false;
  const skillQuestions = React.useMemo(() => {
    if (!isSkillBased) return {};

    return customQuestions.reduce((acc: any, question: any) => {
      if (question.skill) {
        if (!acc[question.skill]) {
          acc[question.skill] = [];
        }
        acc[question.skill].push(question);
      }
      return acc;
    }, {});
  }, [customQuestions, isSkillBased]);

  const renderQuestions = (questions: any[], startIndex = 0) => {
    return questions.map((question, index) => {
      // Map the custom question format to SpecificQuestion props
      const questionProps = {
        key: question._id || index,
        timeTaken: question?.conversationAIStatus
          ? question.conversationRatingAndDuration?.timeTaken || 0
          : question.timeTaken,
        AITextRating: question.AITextRating,
        questionNumber: startIndex + index + 1,
        questionText: question.question,
        transcript: question.answer,
        responseAnalysis: question.ratingReason || "",
        rating: question?.conversationAIStatus
          ? question.conversationRatingAndDuration?.rating || 0
          : question.rating || 0, // Convert 0-10 rating to percentage
        responseType: question.conversationAIStatus
          ? "AI-Assisted Response"
          : "100% Human Response",
        codeBlock: question.codeBlock,
        predefinedAnswer: question.predefinedAnswer,
        conversation: [
          ...(Array.isArray(question.conversationAIQuestions)
            ? question.conversationAIQuestions.map((item: any) => ({
                question: item.question,
                answer: item.answer,
                ratingReason: item.ratingReason,
              }))
            : []),
          ...(Array.isArray(question.follow_up_questions)
            ? question.follow_up_questions.map((item: any) => ({
                question: item.question,
                answer: item.answer,
                ratingReason: item.aiResponseAnalysis,
              }))
            : []),
        ]
          .filter((followUp: any, index: number, arr: any[]) =>
            arr.findIndex((f: any) => f.question === followUp.question) === index
          )
          .map((followUp: any) => [
            {
              type: "question",
              content: followUp.question,
              isCandidate: false,
            },
            {
              type: "transcript",
              content: followUp.answer,
              aiResponse: followUp.ratingReason,
              isCandidate: true,
            },
          ])
          .flat(),
        isGeneral: false,
      };

      return <SpecificQuestion {...questionProps} />;
    });
  };

  return (
    <>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm p-4 md:p-6 mb-3 md:mb-6 border border-blue-100">
        <h1 className="text-lg md:text-xl lg:text-2xl font-semibold text-gray-900 mb-1 md:mb-2">
          Specific Questions
        </h1>
        <p className="text-gray-600 text-xs md:text-sm lg:text-base">
          Evaluation of the candidate's knowledge and suitability for the targeted role.
        </p>
      </div>

      {isSkillBased ? (
        // Render skill-based questions
        Object.entries(skillQuestions).map(([skillName, questions]: [string, any]) => {
          if (!questions || questions.length === 0) return null;

          return (
            <div key={skillName} className="mb-3 md:mb-6">
              <h2 className="text-md md:text-xl font-bold text-gray-900 mb-2 ml-4">{skillName}:</h2>
              {renderQuestions(questions)}
            </div>
          );
        })
      ) : (
        // Render regular custom questions
        <div className="mb-3 md:mb-6">{renderQuestions(customQuestions)}</div>
      )}
    </>
  );
};

export default SpecificQuestionSection;
